import Vue from 'vue'
import Router from 'vue-router'
import { t } from '@/plugin/vue-lang'
import firebase from 'firebase/app';
import 'firebase/auth';
import axios from 'axios'
import format from 'date-fns/format'
import { decodeSessionUser, getCompany } from './store/core/cacheService'
import { retryOnAuthPropagation } from './store/core/authRetry'
import { clearSessionLocalStorage } from './store/core/logoutStorage'
import { ensureChartLibs } from '@/plugin/lazy-chart-libs'
Vue.use(Router)

// All page components are dynamically imported with named webpack chunks so
// the initial JS payload is only the app shell + the destination page.
// Crucially, public routes (/signup, /login, /pincode) never download the
// authed pages (insights, members, skills, recognition...) which were
// previously pulled into the entry chunk via top-level static imports and
// dominated the bundle size on a fresh /signup load.
const LoginPage         = () => import(/* webpackChunkName: "auth" */ '@/components/login.vue')
const SsoMobilePage     = () => import(/* webpackChunkName: "auth" */ '@/components/ssoMobile.vue')
const PincodePage       = () => import(/* webpackChunkName: "auth" */ '@/components/pincode.vue')
const ssoLogin          = () => import(/* webpackChunkName: "auth" */ '@/components/sso.vue')
const ssoLogout         = () => import(/* webpackChunkName: "auth" */ '@/components/sso-logout.vue')

const InsightsPage      = () => import(/* webpackChunkName: "insights" */ '@/components/insights.vue')
const reports           = () => import(/* webpackChunkName: "insights" */ '@/components/reports.vue')
const MembersPage       = () => import(/* webpackChunkName: "members" */ '@/components/members.vue')
const RecognitionPage   = () => import(/* webpackChunkName: "recognition" */ '@/components/recognition.vue')
const PerformancePage   = () => import(/* webpackChunkName: "performance" */ '@/components/performance.vue')
const QuestionsPage     = () => import(/* webpackChunkName: "questions" */ '@/components/questions.vue')
const Skills            = () => import(/* webpackChunkName: "skills" */ '@/components/skills.vue')
const Programs          = () => import(/* webpackChunkName: "programs" */ '@/components/programs.vue')
const RewardsPage       = () => import(/* webpackChunkName: "rewards" */ '@/components/rewards.vue')
const BillingPage       = () => import(/* webpackChunkName: "billing" */ '@/components/billing.vue')
const SettingsPage      = () => import(/* webpackChunkName: "settings" */ '@/components/settings.vue')
const HappilyVillage    = () => import(/* webpackChunkName: "village" */ '@/components/happily-village.vue')
const Integrations      = () => import(/* webpackChunkName: "integrations" */ '@/components/Integrations.vue')
const integrationsZapier = () => import(/* webpackChunkName: "integrations" */ '@/components/integration-zapier.vue')
const slackCallback     = () => import(/* webpackChunkName: "integrations" */ '@/components/Integrations/slack-callback.vue')
const sparks            = () => import(/* webpackChunkName: "sparks" */ '@/components/sparks.vue')
const awards            = () => import(/* webpackChunkName: "awards" */ '@/components/awards.vue')
const documentDeveloper = () => import(/* webpackChunkName: "document-developer" */ '@/components/document-developer.vue')
const setupWizard       = () => import(/* webpackChunkName: "setup-wizard" */ '@/components/setup-wizard.vue')
const register          = () => import(/* webpackChunkName: "legacy-public" */ '@/components/register.vue')
const rewardMessage     = () => import(/* webpackChunkName: "legacy-public" */ '@/components/wv-reward-message.vue')
const relationshipMapping = () => import(/* webpackChunkName: "legacy-public" */ '@/components/wv-relationship-mapping.vue')
const PageNotFound      = () => import(/* webpackChunkName: "not-found" */ '@/components/404.vue')

let defaultPage: any = InsightsPage
const userData = localStorage.getItem('userData')
let user: any;
try {
  user = (userData)?decodeURIComponent(atob(userData)):null
  user = JSON.parse(user)
  if (user && user.user_type == 'admin') {
    defaultPage = MembersPage
  }
} catch (error) {

}

const getStoredSessionUser = () => {
  try {
    return decodeSessionUser(localStorage.getItem('userData'))
  } catch (error) {
    return null
  }
}

// Guard bounces used to be invisible in the auth audit trail — users reported
// "login bounces back to the login page" and the logs showed nothing. This
// writes directly to the logs collection (firestore is loaded lazily so the
// public entry chunk stays lean). Failures are ignored, but the write is
// attempted before sign-out clears its authentication and session metadata.
const logSessionInvalidated = async (reason: string, redirectPath: string, sessionUser: any) => {
  const now = new Date()
  const loginMethod = localStorage.getItem('sso') == 'Y' ? 'sso' : 'pin'
  const sessionId = localStorage.getItem('authSessionId') || ''
  try {
    await import(/* webpackChunkName: "firestore-log" */ 'firebase/firestore')
    await firebase.firestore().collection('logs').add({
      createdate: format(now, 'YYYY-MM-DD HH:mm:ss'),
      createdate_utc: now.toISOString(),
      log_type: 'auth',
      event: 'session_invalidated',
      status: 'session_invalidated',
      result: 'failed',
      reason,
      user: (sessionUser && sessionUser.email) || '',
      company: (sessionUser && sessionUser.company) || '',
      user_type: (sessionUser && sessionUser.user_type) || '',
      is_adviser: !!(sessionUser && sessionUser.is_adviser),
      login_method: loginMethod,
      route: redirectPath,
      user_agent: typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '',
      session_id: sessionId,
    })
  } catch (error) {
    // Logging must never prevent a user from reaching the login screen.
  }
}

const clearInvalidAuthSession = async (redirectPath: string, reason: string) => {
  const sessionUser = getStoredSessionUser()
  await logSessionInvalidated(reason, redirectPath, sessionUser)
  try {
    await firebase.auth().signOut()
  } catch (error) {
    // Continue local cleanup even if Firebase is already signed out.
  }
  clearSessionLocalStorage(sessionUser)
  localStorage.pageBeforeLogin = redirectPath
}


const freetierSignupEnabled = process.env.VUE_APP_FREETIER_SIGNUP_ENABLED === 'true'

const signupRoutes = freetierSignupEnabled
  ? [
      {
        name: 'signup-entry',
        path: '/signup',
        component: () => import(/* webpackChunkName: "signup" */ '@/components/signup/SignupEntry.vue')
      },
      {
        name: 'signup-verify',
        path: '/signup/verify',
        component: () => import(/* webpackChunkName: "signup" */ '@/components/signup/SignupPinVerify.vue')
      },
      {
        name: 'signup-wizard',
        path: '/signup/wizard/:slug([a-z]+)',
        component: () => import(/* webpackChunkName: "signup" */ '@/components/signup/SignupWizard.vue')
      },
      // Back-compat redirect for any in-flight session or external link that
      // still references the old numeric step path.
      {
        path: '/signup/wizard/:step(\\d+)',
        redirect: (to) => {
          const SLUGS = ['company', 'values', 'people', 'rewards', 'launch']
          const n = parseInt(to.params.step as string, 10)
          const slug = (Number.isFinite(n) && n >= 1 && n <= SLUGS.length) ? SLUGS[n - 1] : SLUGS[0]
          return { path: `/signup/wizard/${slug}` }
        }
      },
      // Deprecation shim: while the free-tier flow is on, redirect the
      // legacy /user-registration entry point to /signup. The original
      // component-backed route is unregistered in this branch so the
      // redirect wins; when the flag is off, the legacy route stays put.
      {
        name: 'user-registration',
        path: '/user-registration',
        redirect: '/signup'
      }
    ]
  : []

const routes = new Router({
  mode: 'history',
  routes: [
    ...signupRoutes,
    // no authen
    {
      name: "login",
      path: '/login',
      component: LoginPage
    },
    {
      name: "sso-mobile",
      path: '/sso-mobile',
      component: SsoMobilePage
    },
    {
      name: "pincode",
      path: '/pincode',
      component: PincodePage
    },
    {
      path: '/sso-logout', component: ssoLogout
    },
    {
      name: "sso-login",
      path: '/sso-login/:id',
      component: ssoLogin
    },
    {
      name: "relationship-mapping",
      path: "/relationship-mapping",
      component: relationshipMapping
    },
    {
      name: "reward-message",
      path: "/reward-message",
      component: rewardMessage
    },
    // When the free-tier signup flag is on, /user-registration is
    // redirected to /signup via the prepended `signupRoutes` above. When
    // the flag is off, this component-backed route serves the legacy
    // registration page unchanged.
    ...(freetierSignupEnabled
      ? []
      : [
          {
            name: 'user-registration',
            path: '/user-registration',
            component: register
          }
        ]),
    // with tabs
    {
      name: "index",
      path: '/',
      component: defaultPage,
      meta: {
        auth: true
      }
    },
    {
      name: 'insightsWithTab',
      path: '/insights/:tab',
      component: InsightsPage,
      meta: {
        auth: true
      }
    },
    {
      name: 'insights',
      path: '/insights',
      component: InsightsPage,
      meta: {
        auth: true
      }
    },
    {
      name: "members",
      path: '/members/:tab',
      component: MembersPage,
      meta: {
        auth: true
      }
    },
    {
      path: '/members',
      component: MembersPage,
      meta: {
        auth: true
      }
    },
    // Recognition routes (formerly Power-ups)
    {
      name: "recognition",
      path: '/recognition/:tab',
      component: RecognitionPage,
      meta: {
        auth: true
      }
    },
    {
      name: "recognition-with-id",
      path: '/recognition/:tab/:id',
      component: RecognitionPage,
      meta: {
        auth: true
      }
    },
    {
      path: '/recognition',
      component: RecognitionPage,
      meta: {
        auth: true
      }
    },
    // Performance routes
    {
      name: "performance",
      path: '/performance/:tab',
      component: PerformancePage,
      meta: {
        auth: true
      }
    },
    {
      path: '/performance',
      component: PerformancePage,
      meta: {
        auth: true
      }
    },
    // Power-ups redirects for backward compatibility
    {
      path: '/power-ups/:tab/:id',
      redirect: to => {
        return { path: `/recognition/${to.params.tab}/${to.params.id}` }
      },
      meta: {
        auth: true
      }
    },
    {
      path: '/power-ups/:tab',
      redirect: to => {
        if (to.params.tab === 'performance') {
          return { path: '/performance/overview' }
        }
        return { path: `/recognition/${to.params.tab}` }
      },
      meta: {
        auth: true
      }
    },
    {
      path: '/power-ups',
      redirect: '/recognition',
      meta: {
        auth: true
      }
    },
    {
      name: "questions",
      path: '/questions/:tab',
      component: QuestionsPage,
      meta: {
        auth: true
      }
    },
    {
      path: '/questions',
      component: QuestionsPage,
      meta: {
        auth: true
      }
    },
    {
      name: "skills",
      path: '/skills/:tab',
      component: Skills,
      meta: {
        auth: true
      }
    },
    {
      path: '/skills',
      component: Skills,
      meta: {
        auth: true
      }
    },
    {
      name: "programs",
      path: '/programs/:tab',
      component: Programs,
      meta: {
        auth: true
      }
    },
    {
      path: '/programs',
      component: Programs,
      meta: {
        auth: true
      }
    },
    {
      name: "rewards",
      path: '/rewards/:tab',
      component: RewardsPage,
      meta: {
        auth: true
      }
    },
    {
      path: '/rewards',
      component: RewardsPage,
      meta: {
        auth: true
      }
    },
    
    {
      name: "happily-village",
      path: '/happily-village/:tab',
      component: HappilyVillage,
      meta: {
        auth: true
      }
    },
    {
      path: '/happily-village',
      component: HappilyVillage,
      meta: {
        auth: true
      }
    },
    {
      name: "slack-oauth-callback",
      path: '/integrations/slack/callback',
      component: slackCallback,
      meta: {
        auth: true
      }
    },
    {
      name: "integrations",
      path: '/integrations/:tab',
      component: Integrations,
      meta: {
        auth: true
      }
    },
    {
      path: '/integrations',
      component: Integrations,
      meta: {
        auth: true
      }
    },
    {
      name: "document-developer",
      path: '/document-developer/:tab',
      component: documentDeveloper,
      meta: {
        auth: true
      }
    },
    {
      path: '/document-developer',
      component: documentDeveloper,
      meta: {
        auth: true
      }
    },
    {
      name: "sparks",
      path: "/sparks/:tab",
      component: sparks,
      meta: {
        auth: true
      }
    },
    {
      path: "/sparks",
      component: sparks,
      meta: {
        auth: true
      }
    },
    {
      name: "reports",
      path: "/reports/:tab",
      component: reports,
      meta: {
        auth: true
      }
    },
    {
      path: "/reports",
      component: reports,
      meta: {
        auth: true
      }
    },
    {
      name: "awards",
      path: "/awards/:tab",
      component: awards,
      meta: {
        auth: true
      }
    },
    {
      path: "/awards",
      component: awards,
      meta: {
        auth: true
      }
    },
    // sigle tab
    {
      name: "billing",
      path: '/billing',
      component: BillingPage,
      meta: {
        auth: true
      }
    },
    {
      name: "settings",
      path: '/settings',
      component: SettingsPage,
      meta: {
        auth: true
      }
    },
    {
      name: "setup-wizard",
      path: "/setup-wizard",
      component: setupWizard,
      meta: {
        auth: true
      }
    },
    {
      name: "integration-zapier",
      path: "/integration-zapier",
      component: integrationsZapier,
      meta: {
        auth: true
      }
    },
    {
      name: "404",
      path: "*",
      component: PageNotFound,
      meta: {
        auth: true
      }
    }
  ]
})
const routePathToI18nKey: Record<string, string | false> = {
  "login": "page.login_title",
  "pincode": "page.pincode_title",
  "sso-login": "page.login_title",
  "insights": "page.insights",
  "": false,
  "members": "page.members",
  "newmembers": "page.members",
  "power-ups": "page.power_up",
  "recognition": "page.recognition",
  "performance": "page.performance",
  "questions": "page.questions",
  "skills": "page.skills",
  "programs": "page.programs",
  "rewards": "page.rewards",
  "billing": "page.billing",
  "settings": "page.settings",
  "happily-village": "page.happilyVillage",
  "integrations": "page.integrations",
  "relationship-mapping": "page.relationship_mapping",
  "reward-message": "page.reward_message",
  "user-registration": "page.user_registration",
  "setup-wizard": "page.setup_wizard",
  "sparks": "page.sparks",
  "document-developer": "page.documentation_dev",
}

routes.beforeEach(async (to, from, next) => {
  const currentPate = to.path.split('/')[1]
  const i18nKey = routePathToI18nKey[currentPate]
  if (i18nKey) {
    document.title = `My Happily - ${t(i18nKey)}`
  } else {
    document.title = "My Happily"
  }

  if (to.matched.some(record => record.meta.auth)) {
    // Authed routes use Highcharts/Chart.js — pull them in (idempotent) before
    // the destination page mounts. Public pages skip this.
    await ensureChartLibs()
    if (!firebase.auth().currentUser) {
      await clearInvalidAuthSession(to.path, 'no_firebase_user')
      next({
        path: '/login'
      })
    } else {
      // Re-read the session on every navigation. The module-level snapshot
      // parsed at page load goes stale after any in-SPA re-login (account
      // switcher, post-bounce retry, adviser PIN login) and used to check
      // the fresh session against the PREVIOUS user's company and IP rules.
      const sessionUser = getStoredSessionUser()
      if (sessionUser && sessionUser.company) {
        let company = null
        try {
          company = await retryOnAuthPropagation(() => getCompany(sessionUser.company))
        } catch (error) {
          console.warn('[router] company lookup failed in auth guard:', error)
          await clearInvalidAuthSession(to.path, 'company_lookup_failed')
          next({ path: '/login' })
          return
        }
        if (company && company.ips) {
          let ip = '';
          const url = 'https://api.ipify.org?format=json'
          await axios.get(url).then(response => {
            ip = response.data.ip
          })
          .catch(function (error) {
            console.log(error);
          });
          // IP-restricted companies fail closed: inability to verify the
          // public IP cannot bypass the allowlist.
          if (!ip || !company.ips.includes(ip)) {
            await clearInvalidAuthSession(to.path, ip ? 'ip_blocked' : 'ip_lookup_failed');
            next({
              path: '/login'
            })
            return
          }
        }
      }
      next()
    }
  } else {
    next()
  }
})
export default routes
